<template>
  <div>
    <PageContainer
      v-if="loading"
      class="profile-container d-flex align_center justify_center"
      :class="{ isDarkMode }"
      ><NumeraiLoader
    /></PageContainer>
    <PageNotFound v-else-if="show404" />
    <template v-else>
      <EditAccountProfileModal
        :dialog="showEditModal"
        :account-meta="profile"
        :discord-link-token="accountInfo.discordLinkToken"
        :on-close="onEditModalClose"
        :on-update="fetchAccountProfile"
      />
      <PageContainer class="profile-container" :class="{ isDarkMode }">
        <div class="body-container">
          <NumeraiPaper class="pa-6">
            <div class="heading">
              <div
                class="profile-pic-container"
                :class="{ editable: isAccountOwner }"
              >
                <NumeraiTooltip
                  :text-list="highestEarnedTitleDetails"
                  :disable="!highestEarnedTitleDetails"
                >
                  <div
                    class="profile-pic"
                    :class="{
                      default: !profile.profileUrl,
                      noTitle: !highestEarnedTitle,
                    }"
                    :style="
                      profile.profileUrl
                        ? { backgroundImage: `url('${profile.profileUrl}')` }
                        : null
                    "
                  ></div>
                  <div
                    v-if="highestEarnedTitle"
                    class="gm-title"
                    :class="{ [highestEarnedTitle]: highestEarnedTitle }"
                  >
                    {{ highestEarnedTitle }}
                  </div>
                  <div v-if="isAccountOwner" class="profile-pic-edit">
                    <NumeraiUploadFile
                      :auth-endpoint="$client.getAccountProfileImageUploadAuth"
                      :size-limit="200000"
                      success-text="Profile image uploaded"
                      @upload="fetchAccountProfile"
                    >
                      <button class="numerai-button">
                        Upload photo
                      </button>
                    </NumeraiUploadFile>
                  </div>
                </NumeraiTooltip>
              </div>
              <div class="account-meta">
                <h1 class="account-name">
                  {{
                    profile.displayName
                      ? profile.displayName.toUpperCase()
                      : profile.username.toUpperCase()
                  }}
                  <span v-if="profile.team"
                    ><img
                      src="/img/Numerai-Sigil-Black.png"
                      class="team-badge"
                      :class="{ darkMode: isDarkMode }"
                  /></span>
                </h1>
                <p class="inception">
                  {{
                    `${profile.location ? `${profile.location} · ` : ''}` +
                      `Joined ${sinceJoinDate}`
                  }}
                </p>
                <p class="tagline">
                  {{ profile.bio }}
                </p>
                <div class="d-flex align_center mb-3">
                  <ExternalLink
                    v-if="profile.discord.username"
                    :url="
                      `https://discordapp.com/users/${profile.discord.userId}`
                    "
                    class="text_decoration_none cursor-pointer"
                    ><v-icon small color="#1976d2" class="mr-2"
                      >mdi-discord</v-icon
                    ></ExternalLink
                  >
                  <ExternalLink
                    v-if="profile.github"
                    :url="
                      `https://github.com/${encodeURIComponent(profile.github)}`
                    "
                    class="text_decoration_none cursor-pointer"
                    ><v-icon small color="#1976d2" class="mr-2"
                      >mdi-github-circle</v-icon
                    ></ExternalLink
                  >
                  <ExternalLink
                    v-if="profile.twitter"
                    :url="`https://twitter.com/${profile.twitter}`"
                    class="text_decoration_none cursor-pointer"
                  >
                    <v-icon small color="#1976d2" class="mr-2"
                      >mdi-twitter</v-icon
                    ></ExternalLink
                  >
                  <ExternalLink
                    v-if="profile.linkedin"
                    :url="`https://linkedin.com/in/${profile.linkedin}`"
                    class="text_decoration_none cursor-pointer"
                  >
                    <v-icon small color="#1976d2" class="mr-2"
                      >mdi-linkedin-box</v-icon
                    ></ExternalLink
                  >
                  <ExternalLink
                    v-if="profile.kaggle"
                    :url="`https://kaggle.com/${profile.kaggle}`"
                  >
                    <div class="kaggle-icon">
                      <img :src="require('@/assets/kaggle_icon.svg')" />
                    </div>
                  </ExternalLink>
                  <ExternalLink
                    v-if="profile.website"
                    :url="profile.website"
                    :text="profile.website"
                  />
                </div>
                <a v-if="isAccountOwner" @click="() => (showEditModal = true)"
                  ><p>Edit account profile</p></a
                >
              </div>
            </div>
            <div class="stats">
              <div class="stat">
                <NumeraiTooltip text="1Y Avg MMC20 Rank">
                  <div class="label">MMC20 Rank</div>
                </NumeraiTooltip>
                <div class="value">
                  {{
                    profile.scores && profile.scores.mmcLtmRank
                      ? profile.scores.mmcLtmRank.toLocaleString()
                      : '-'
                  }}<span
                    v-if="profile.scores && profile.scores.mmcLtmRank"
                    class="superscript"
                    >{{ suffix(profile.scores.mmcLtmRank) }}</span
                  >
                </div>
                <div class="sub-label">of {{ localizedTotalAccounts }}</div>
              </div>
              <div class="stat">
                <div v-if="isAppClassic()" class="stat">
                  <NumeraiTooltip text="1Y Avg CORR20v2 Rank">
                    <div class="label">CORR20 Rank</div>
                  </NumeraiTooltip>
                  <div class="value">
                    {{
                      profile.scores && profile.scores.v2Corr20LtmRank
                        ? profile.scores.v2Corr20LtmRank.toLocaleString()
                        : '-'
                    }}<span
                      v-if="profile.scores && profile.scores.v2Corr20LtmRank"
                      class="superscript"
                      >{{ suffix(profile.scores.v2Corr20LtmRank) }}</span
                    >
                  </div>
                </div>
                <div v-if="isAppSignals()" class="stat">
                  <NumeraiTooltip text="1Y Avg FNCv4 Rank">
                    <div class="label">FNC Rank</div>
                  </NumeraiTooltip>
                  <div class="value">
                    {{
                      profile.scores && profile.scores.fncV4LtmRank
                        ? profile.scores.fncV4LtmRank.toLocaleString()
                        : '-'
                    }}<span
                      v-if="profile.scores && profile.scores.fncV4LtmRank"
                      class="superscript"
                      >{{ suffix(profile.scores.fncV4LtmRank) }}</span
                    >
                  </div>
                </div>
                <div v-if="isAppCryptoSignals()" class="stat">
                  <NumeraiTooltip text="1Y Avg CORR Rank">
                    <div class="label">CORR Rank</div>
                  </NumeraiTooltip>
                  <div class="value">
                    {{
                      profile.scores && profile.scores.corrLtmRank
                        ? profile.scores.corrLtmRank.toLocaleString()
                        : '-'
                    }}<span
                      v-if="profile.scores && profile.scores.corrLtmRank"
                      class="superscript"
                      >{{ suffix(profile.scores.corrLtmRank) }}</span
                    >
                  </div>
                </div>
                <div class="sub-label">of {{ localizedTotalAccounts }}</div>
              </div>
              <div v-if="isAppClassic()" class="stat">
                <NumeraiTooltip text="1Y Avg MMC60 Rank">
                  <div class="label">MMC60 Rank</div>
                </NumeraiTooltip>
                <div class="value">
                  {{
                    profile.scores && profile.scores.mmc60LtmRank
                      ? profile.scores.mmc60LtmRank.toLocaleString()
                      : '-'
                  }}<span
                    v-if="profile.scores && profile.scores.mmc60LtmRank"
                    class="superscript"
                    >{{ suffix(profile.scores.mmc60LtmRank) }}</span
                  >
                </div>
              </div>
              <div v-if="isAppClassic()" class="stat">
                <NumeraiTooltip text="1Y Avg CORR60 Rank">
                  <div class="label">CORR60 Rank</div>
                </NumeraiTooltip>
                <div class="value">
                  {{
                    profile.scores && profile.scores.corr60LtmRank
                      ? profile.scores.corr60LtmRank.toLocaleString()
                      : '-'
                  }}<span
                    v-if="profile.scores && profile.scores.corr60LtmRank"
                    class="superscript"
                    >{{ suffix(profile.scores.corr60LtmRank) }}</span
                  >
                </div>
              </div>
              <div class="stat">
                <NumeraiTooltip :text="seasonRankTooltip">
                  <div class="label">Season Rank</div>
                </NumeraiTooltip>
                <div class="value">
                  {{
                    profile.scores && profile.scores.seasonRank
                      ? profile.scores.seasonRank.toLocaleString()
                      : '-'
                  }}<span
                    v-if="profile.scores && profile.scores.seasonRank"
                    class="superscript"
                    >{{ suffix(profile.scores.seasonRank) }}</span
                  >
                </div>
                <div class="sub-label">of {{ totalSeasonParticipants }}</div>
              </div>
              <div class="stat">
                <div class="label">Stake</div>
                <div class="value" :class="{ nmr: !!totalNmr }">
                  {{ totalNmr }} {{ totalNmr ? ' NMR' : '-' }}
                </div>
                <div class="sub-label">{{ nmrStakeToUsd }}</div>
              </div>
              <div class="stat">
                <div class="label">1Y Return</div>
                <div
                  class="value return d-flex align_center"
                  :class="{
                    'color-subtle': totalReturn === 0,
                    'color-positive': totalReturn > 0,
                    'color-negative': totalReturn < 0,
                  }"
                >
                  {{ totalReturn !== 0 ? totalReturn.toLocaleString() : '-'
                  }}{{ totalReturn !== 0 ? '%' : '' }}
                </div>
                <div class="sub-label">{{ totalReturnNmr }}</div>
              </div>
            </div>
          </NumeraiPaper>
          <NumeraiChart
            header="MMC"
            class="mt-3"
            :description="
              (isCumulativeDisplay.mmc ? 'Cumulative s' : 'S') +
                'take-weighted-average MMC score of all models over the past 1 year' +
                (isNotBenchmarkAccount ? ' vs Numerai benchmark models' : '')
            "
            :chart-options="{
              chartData: getGraphDataForScore('mmc', 'mmc'),
              dataLabels: ['MMC20', 'Numerai Benchmark MMC20'],
              chartColors: ['#ff6e40', 'rgb(128 128 128 / 65%)'],
              dashedLines: [null, null],
              legend: false,
              area: false,
              verticalGridLines: true,
              showyTicks: true,
              drawYBorder: true,
            }"
          >
            <template #header-right>
              <span v-if="isNotBenchmarkAccount" class="benchmark-link mr-3">
                <v-icon>mdi-open-in-new</v-icon>
                <a :href="`/~${benchmarkAccount}`"> Numerai Benchmark Models</a>
              </span>
              <v-menu
                left
                bottom
                offset-y
                :dark="isDarkMode"
                :close-on-content-click="false"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon v-bind="attrs" v-on="on">
                    <v-icon
                      :color="
                        isDarkMode
                          ? 'var(--color-secondary)'
                          : 'var(--color-dark)'
                      "
                    >
                      mdi-settings
                    </v-icon>
                  </v-btn>
                </template>
                <v-card>
                  <v-list>
                    <v-list-item>
                      <v-list-item-action>
                        <v-switch
                          v-model="isCumulativeDisplay.mmc"
                          color="green"
                          dense
                        ></v-switch>
                      </v-list-item-action>
                      <v-list-item-title>Cumulative</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-card>
              </v-menu>
            </template>
          </NumeraiChart>
          <NumeraiChart
            v-if="isAppClassic()"
            class="mt-3"
            header="CORR"
            :description="
              (isCumulativeDisplay.corr ? 'Cumulative s' : 'S') +
                'take-weighted-average CORR score of all models over the past 1 year' +
                (isNotBenchmarkAccount ? ' vs Numerai benchmark models' : '')
            "
            :chart-options="{
              chartData: getGraphDataForScore('corr', 'corr20v2'),
              dataLabels: ['CORR20', 'Numerai Benchmark CORR20'],
              chartColors: [
                isDarkMode ? '#ffffff' : 'rgb(49 49 49)',
                'rgb(128 128 128 / 65%)',
              ],
              dashedLines: [null, null],
              legend: false,
              area: false,
              verticalGridLines: true,
              showyTicks: true,
              drawYBorder: true,
            }"
          >
            <template #header-right>
              <span v-if="isNotBenchmarkAccount" class="benchmark-link mr-3">
                <v-icon>mdi-open-in-new</v-icon>
                <a :href="`/~${benchmarkAccount}`"> Numerai Benchmark Models</a>
              </span>
              <v-menu
                left
                bottom
                offset-y
                :dark="isDarkMode"
                :close-on-content-click="false"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon v-bind="attrs" v-on="on">
                    <v-icon
                      :color="
                        isDarkMode
                          ? 'var(--color-secondary)'
                          : 'var(--color-dark)'
                      "
                    >
                      mdi-settings
                    </v-icon>
                  </v-btn>
                </template>
                <v-card>
                  <v-list>
                    <v-list-item>
                      <v-list-item-action>
                        <v-switch
                          v-model="isCumulativeDisplay.corr"
                          color="green"
                          dense
                        ></v-switch>
                      </v-list-item-action>
                      <v-list-item-title>Cumulative</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-card>
              </v-menu>
            </template>
          </NumeraiChart>
          <NumeraiChart
            v-if="isAppSignals()"
            class="mt-3"
            header="FNCv4"
            :description="
              (isCumulativeDisplay.fnc ? 'Cumulative s' : 'S') +
                'take-weighted-average FNCv4 score of all models over the past 1 year' +
                (isNotBenchmarkAccount ? ' vs Numerai benchmark models' : '')
            "
            :chart-options="{
              chartData: graphData.fncv4
                ? [
                    isCumulativeDisplay.fnc
                      ? cumSum(graphData.fncv4)
                      : graphData.fncv4,
                    isNotBenchmarkAccount
                      ? isCumulativeDisplay.fnc
                        ? cumSum(benchmarkGraphData.fncv4)
                        : benchmarkGraphData.fncv4
                      : [],
                  ]
                : [],
              dataLabels: ['FNCv4', 'Numerai Benchmark FNCv4'],
              area: false,
              chartColors: [
                isDarkMode ? '#ffffff' : 'rgb(49 49 49)',
                'rgb(128 128 128 / 65%)',
              ],
              verticalGridLines: true,
              showyTicks: true,
              drawYBorder: true,
            }"
          >
            <template #header-right>
              <span v-if="isNotBenchmarkAccount" class="benchmark-link mr-3">
                <v-icon>mdi-open-in-new</v-icon>
                <a :href="`/~${benchmarkAccount}`"> Numerai Benchmark Models</a>
              </span>
              <v-menu
                left
                bottom
                offset-y
                :dark="isDarkMode"
                :close-on-content-click="false"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon v-bind="attrs" v-on="on">
                    <v-icon
                      :color="
                        isDarkMode
                          ? 'var(--color-secondary)'
                          : 'var(--color-dark)'
                      "
                    >
                      mdi-settings
                    </v-icon>
                  </v-btn>
                </template>
                <v-card>
                  <v-list>
                    <v-list-item>
                      <v-list-item-action>
                        <v-switch
                          v-model="isCumulativeDisplay.fnc"
                          color="green"
                          dense
                        ></v-switch>
                      </v-list-item-action>
                      <v-list-item-title>Cumulative</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-card>
              </v-menu>
            </template>
          </NumeraiChart>
          <NumeraiChart
            v-if="isAppCryptoSignals()"
            class="mt-3"
            header="CORR"
            :description="
              (isCumulativeDisplay.corr ? 'Cumulative s' : 'S') +
                'take-weighted-average CORR score of all models over the past 1 year' +
                (isNotBenchmarkAccount ? ' vs Numerai benchmark models' : '')
            "
            :chart-options="{
              chartData: graphData.corr
                ? [
                    isCumulativeDisplay.corr
                      ? cumSum(graphData.corr)
                      : graphData.corr,
                    isNotBenchmarkAccount
                      ? isCumulativeDisplay.corr
                        ? cumSum(benchmarkGraphData.corr)
                        : benchmarkGraphData.corr
                      : [],
                  ]
                : [],
              dataLabels: ['CORR', 'Numerai Benchmark CORR'],
              area: false,
              chartColors: [
                isDarkMode ? '#ffffff' : 'rgb(49 49 49)',
                'rgb(128 128 128 / 65%)',
              ],
              verticalGridLines: true,
              showyTicks: true,
              drawYBorder: true,
            }"
          >
            <template #header-right>
              <span v-if="isNotBenchmarkAccount" class="benchmark-link mr-3">
                <v-icon>mdi-open-in-new</v-icon>
                <a :href="`/~${benchmarkAccount}`"> Numerai Benchmark Models</a>
              </span>
              <v-menu
                left
                bottom
                offset-y
                :dark="isDarkMode"
                :close-on-content-click="false"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon v-bind="attrs" v-on="on">
                    <v-icon
                      :color="
                        isDarkMode
                          ? 'var(--color-secondary)'
                          : 'var(--color-dark)'
                      "
                    >
                      mdi-settings
                    </v-icon>
                  </v-btn>
                </template>
                <v-card>
                  <v-list>
                    <v-list-item>
                      <v-list-item-action>
                        <v-switch
                          v-model="isCumulativeDisplay.corr"
                          color="green"
                          dense
                        ></v-switch>
                      </v-list-item-action>
                      <v-list-item-title>Cumulative</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-card>
              </v-menu>
            </template>
          </NumeraiChart>
          <NumeraiChart
            class="mt-3"
            header="Stake"
            :description="'Total stake value of all models in the past 1 year'"
            :chart-options="{
              chartData: graphData.stakes ? [graphData.stakes] : [],
              dataLabels: ['Stake'],
              area: true,
              verticalGridLines: true,
              showyTicks: true,
              drawYBorder: true,
            }"
          ></NumeraiChart>
          <NumeraiPaper v-if="showModels" class="section-container mt-3 pa-6">
            <div class="section-title mb-2">Models</div>
            <v-data-table
              :headers="modelTableHeaders"
              :items="visibleModels"
              :sort-by="'stake'"
              :sort-desc="true"
              item-key="name"
              hide-default-footer
              disable-pagination
              :dark="isDarkMode"
            >
              <template v-slot:item.displayName="{ item }">
                <a :href="`/${item.displayName}`" class="text_decoration_none">
                  <div class="flexrow align_center">
                    <img
                      v-if="item.profileUrl"
                      :src="item.profileUrl"
                      class="model-profile-pic mr-2"
                    />
                    <div
                      v-else
                      class="model-profile-pic default-pic mr-2"
                    ></div>
                    <span class="model-name">{{
                      item.displayName.toUpperCase()
                    }}</span>
                  </div>
                </a>
              </template>
              <template v-slot:item.corr20V2Rep="{ item }">
                <p v-if="item.corr20V2Rep" class="font-number mb-0">
                  {{ formatScore(item.corr20V2Rep) }}
                </p>
                <div v-else class="null-number"></div>
              </template>
              <template v-slot:item.fncV4Rep="{ item }">
                <p v-if="item.fncV4Rep" class="font-number mb-0">
                  {{ formatScore(item.fncV4Rep) }}
                </p>
                <div v-else class="null-number"></div>
              </template>
              <template v-slot:item.corrRep="{ item }">
                <p v-if="item.corrRep" class="font-number mb-0">
                  {{ formatScore(item.corrRep) }}
                </p>
                <div v-else class="null-number"></div>
              </template>
              <template v-slot:item.mmcRep="{ item }">
                <p v-if="item.mmcRep" class="font-number mb-0">
                  {{ formatScore(item.mmcRep) }}
                </p>
                <div v-else class="null-number"></div>
              </template>
              <template v-slot:item.mmc60Rep="{ item }">
                <p v-if="item.mmcRep" class="font-number mb-0">
                  {{ formatScore(item.mmc60Rep) }}
                </p>
                <div v-else class="null-number"></div>
              </template>
              <template v-slot:item.corr60Rep="{ item }">
                <p v-if="item.mmcRep" class="font-number mb-0">
                  {{ formatScore(item.corr60Rep) }}
                </p>
                <div v-else class="null-number"></div>
              </template>
              <template v-slot:item.stake="{ item }">
                <p
                  v-if="item.stake && parseFloat(item.stake) !== 0"
                  class="font-number color-nmr mb-0 text-right"
                >
                  <NumeraiTooltip :text="totalStakeToUSDToString(item.stake)">
                    {{ formatCurrency(item.stake) }} NMR
                  </NumeraiTooltip>
                </p>
                <div v-else class="null-number text-right"></div>
              </template>
              <template v-slot:item.return1y="{ item }">
                <p
                  class="text-right font-number mb-0"
                  :class="{
                    delta: item.return1y,
                    'null-number': !item.return1y,
                    'color-positive': parseFloat(item.return1y) > 0,
                    'color-negative': parseFloat(item.return1y) < 0,
                  }"
                >
                  {{
                    item.return1y !== 0
                      ? removeMinusSign(formatPercent(item.return1y))
                      : null
                  }}
                </p>
              </template>
            </v-data-table>
          </NumeraiPaper>
        </div>
      </PageContainer>
    </template>
  </div>
</template>
<script>
import { mapState, mapGetters } from 'vuex'
import {
  NumeraiLoader,
  PageContainer,
  EditAccountProfileModal,
  NumeraiUploadFile,
  NumeraiTooltip,
  NumeraiPaper,
} from '@/components/shared'
import PageNotFound from '@/pages/PageNotFound'
import NumeraiChart from '@/components/charts/NumeraiChart'
import ExternalLink from '@/components/shared/ExternalLink'
import {
  isAppClassic,
  isAppSignals,
  isAppCryptoSignals,
  formatCurrency,
  removeMinusSign,
  formatPercent,
  formatScore,
  getTournamentNumber,
} from '@/lib/utils'
import _ from 'lodash'
import moment from 'moment'

export default {
  components: {
    NumeraiPaper,
    NumeraiLoader,
    NumeraiTooltip,
    PageContainer,
    EditAccountProfileModal,
    NumeraiUploadFile,
    NumeraiChart,
    PageNotFound,
    ExternalLink,
  },
  props: {
    accountname: { type: String, default: null },
  },
  data() {
    return {
      loading: false,
      profile: null,
      totalAccounts: null,
      mostRecentSeasonWithScores: null,
      graphData: {},
      benchmarkGraphData: {},
      isCumulativeDisplay: {
        tc: true,
        corr: true,
        fnc: true,
        mmc: true,
      },
      showEditModal: false,
      show404: false,
    }
  },
  computed: {
    ...mapState(['accountInfo']),
    ...mapGetters(['isLoggedIn', 'nmrPrice', 'isDarkMode']),
    formattedWebsite() {
      if (this.profile.website.startsWith('http')) {
        return this.profile.website
      } else {
        // if the website doesn't start with http,
        // add "//" to indicate a new root address
        return `//${this.profile.website}`
      }
    },
    highestEarnedTitleDetails() {
      if (
        this.profile &&
        this.profile.achievements &&
        this.profile.achievements.length
      ) {
        return this.profile.achievements.map(achievement => {
          let typeLabel = {
            canon_tc: 'TC',
            canon_corr: this.isAppSignals() ? 'FNC' : 'CORR',
            season_score: 'Season',
          }[achievement.type]
          return `${achievement.season} ${typeLabel} ${achievement.tier}`
        })
      }
      return null
    },
    highestEarnedTitle() {
      return this.profile && this.profile.title
    },
    isAccountOwner() {
      if (this.isLoggedIn && this.profile) {
        if (
          this.accountInfo &&
          this.accountInfo.username === this.profile.username
        ) {
          return true
        }
      }
      return false
    },
    benchmarkAccount() {
      return 'benchmark_models'
    },
    isNotBenchmarkAccount() {
      return isAppClassic() && this.accountname !== this.benchmarkAccount
    },
    sinceJoinDate() {
      return moment(this.profile.startDate).fromNow()
    },
    localizedTotalAccounts() {
      return this.totalAccounts.toLocaleString('en-US')
    },
    totalSeasonParticipants() {
      return this.mostRecentSeasonWithScores.participants.toLocaleString(
        'en-US',
      )
    },
    seasonRankTooltip() {
      return `Season ${this.mostRecentSeasonWithScores.year} Rank`
    },
    totalNmr() {
      return formatCurrency(this.profile.totalStake)
    },
    nmrStakeToUsd() {
      const totalStake = parseFloat(this.profile.totalStake) || 0
      if (!totalStake) {
        return null
      }
      const totalUsd = totalStake * parseFloat(this.nmrPrice).toPrecision(3)
      const isNegative = totalUsd < 0
      const formattedTotal = isNegative ? totalUsd * -1 : totalUsd
      return `${isNegative ? '-' : ''}$${_.round(
        formattedTotal,
        formattedTotal >= 100 ? 0 : 2,
      ).toLocaleString()}`
    },
    totalReturn() {
      if (!this.profile.returns || !this.profile.returns.oneYear) {
        return 0
      }

      let value = this.profile.returns.oneYear
      value = _.round(value, 0)

      if (value === 0) {
        return 0
      }

      return value
    },
    totalReturnNmr() {
      if (!this.profile.returns || !this.profile.returns.oneYear) {
        return null
      }
      const nmrReturn = parseFloat(this.profile.returns.oneYearNmr)
      const totalUsd = nmrReturn * parseFloat(this.nmrPrice).toPrecision(3)
      const nmrReturnToUsd = _.round(totalUsd, 2)
      const isNegative = nmrReturnToUsd < 0
      if (nmrReturnToUsd === 0) {
        return '$0.00'
      }

      return `${isNegative ? '-' : ''}$${
        isNegative
          ? (nmrReturnToUsd * -1).toLocaleString()
          : nmrReturnToUsd.toLocaleString()
      }`
    },
    showModels() {
      return this.profile && this.profile.models && this.profile.models.length
    },
    visibleModels() {
      if (this.isNotBenchmarkAccount) {
        return this.profile.models
      }
      // hide certain models from the benchmark_models page
      return this.profile.models.filter(
        model =>
          !model.displayName.startsWith('deprecated_') &&
          model.displayName !== 'old_data_datestamp',
      )
    },
    modelTableHeaders() {
      let res = [
        {
          text: 'Name',
          align: 'start',
          sortable: true,
          value: 'displayName',
        },
      ]

      if (this.isAppClassic()) {
        res.push(
          {
            text: '1Y Avg CORR20',
            align: 'end',
            sortable: true,
            value: 'corr20V2Rep',
          },
          {
            text: '1Y Avg MMC20',
            align: 'end',
            sortable: true,
            value: 'mmcRep',
          },
          {
            text: '1Y Avg CORR60',
            align: 'end',
            sortable: true,
            value: 'corr60Rep',
          },
          {
            text: '1Y Avg MMC60',
            align: 'end',
            sortable: true,
            value: 'mmc60Rep',
          },
        )
      } else if (this.isAppSignals()) {
        res.push(
          {
            text: '1Y Avg FNC',
            align: 'end',
            sortable: true,
            value: 'fncV4Rep',
          },
          {
            text: '1Y Avg MMC',
            align: 'end',
            sortable: true,
            value: 'mmcRep',
          },
        )
      } else if (this.isAppCryptoSignals()) {
        res.push(
          {
            text: '1Y Avg CORR',
            align: 'end',
            sortable: true,
            value: 'corrRep',
          },
          {
            text: '1Y Avg MMC',
            align: 'end',
            sortable: true,
            value: 'mmcRep',
          },
        )
      }

      res.push(
        {
          text: 'Stake',
          align: 'end',
          sortable: true,
          value: 'stake',
        },
        {
          text: '1Y Return',
          align: 'end',
          sortable: true,
          value: 'return1y',
        },
      )
      return res
    },
  },
  watch: {
    // whenever the username changes, we need to reload the profile
    // this happens when you are on someone elses profile and you
    // click `profile` to go to your own
    '$route.params.accountname': async function() {
      this.showEditModal = false
      this.show404 = false
      await this.fetchAccountProfile()
    },
  },
  async created() {
    this.fetchAccountProfile()
    if (this.isNotBenchmarkAccount) {
      const fetchBenchmarkAccount = true
      this.fetchAccountProfile(fetchBenchmarkAccount)
    }
  },
  methods: {
    isAppCryptoSignals,
    isAppClassic,
    isAppSignals,
    formatCurrency,
    formatPercent,
    removeMinusSign,
    formatScore,
    async fetchAccountProfile(fetchBenchmarkAccount) {
      try {
        this.loading = true
        const profile = await this.$client.getAccountProfile(
          fetchBenchmarkAccount ? this.benchmarkAccount : this.accountname,
          getTournamentNumber(),
        )

        if (!fetchBenchmarkAccount) {
          this.profile = profile
        }

        if (!fetchBenchmarkAccount && !this.profile) {
          this.show404 = true
          return
        }

        let tournament = 8
        if (this.isAppSignals()) {
          tournament = 11
        } else if (this.isAppCryptoSignals()) {
          tournament = 12
        }
        this.totalAccounts = await this.$client.getTournamentTotalAccounts(
          tournament,
        )

        // use the most recent season that has scores
        let seasonSummaries = await this.$client.getSeasonParticipants(
          getTournamentNumber(),
        )
        // Filter seasons that have scores
        let seasonsWithScores = seasonSummaries.filter(
          season => season.hasScores,
        )
        // Sort the seasons by year in descending order
        seasonsWithScores.sort((a, b) => b.year - a.year)
        // The most recent season with scores is the first one in the sorted array
        this.mostRecentSeasonWithScores =
          seasonsWithScores.length > 0 ? seasonsWithScores[0] : null

        const tempGraphData = {}

        tempGraphData.stakes = this.formatData(
          profile.totalStakeTs,
          'date',
          'value',
          parseFloat,
        )
        tempGraphData.returns = this.formatData(
          profile.returnsTs.reverse(),
          'date',
          'allTime',
        )
        tempGraphData.mmc = this.formatData(
          profile.scoresTs.toReversed(),
          'date',
          'mmc',
        )

        if (isAppClassic()) {
          tempGraphData.corr20v2 = this.formatData(
            profile.scoresTs.toReversed(),
            'date',
            'v2Corr20',
          )
        } else if (isAppSignals()) {
          tempGraphData.fncv4 = this.formatData(
            profile.scoresTs.reverse(),
            'date',
            'fncV4',
          )
        } else if (isAppCryptoSignals()) {
          tempGraphData.corr = this.formatData(
            profile.scoresTs.reverse(),
            'date',
            'corr',
          )
        }

        const startDate = tempGraphData.mmc.length
          ? tempGraphData.mmc[0].x
          : null
        const endDate = startDate
          ? moment(tempGraphData.mmc[tempGraphData.mmc.length - 1].x)
              .add(1, 'd')
              .format('YYYY-MM-DD')
          : null
        if (
          startDate &&
          tempGraphData.returns.length !== tempGraphData.mmc.length
        ) {
          tempGraphData.returns = this.fillGraphData(
            tempGraphData.returns,
            0,
            startDate,
            endDate,
          )
        }

        if (
          startDate &&
          tempGraphData.stakes.length !== tempGraphData.mmc.length
        ) {
          tempGraphData.stakes = this.fillGraphData(
            tempGraphData.stakes,
            0,
            startDate,
            endDate,
          )
        }

        if (fetchBenchmarkAccount) {
          this.benchmarkGraphData = tempGraphData
        } else {
          this.graphData = tempGraphData
        }
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    fillGraphData(graphData, startValue, startDate, endDate) {
      const end = endDate || graphData[graphData.length - 1].x
      const tempGraphData = []
      let prevValue = startValue
      let i = 0
      for (let m = moment(startDate); m.isBefore(end); m.add(1, 'days')) {
        const point = graphData[i]
        if (!point || m.isBefore(point.x)) {
          tempGraphData.push({
            x: m.format('YYYY-MM-DD'),
            y: prevValue,
          })
        } else {
          tempGraphData.push(graphData[i])
          prevValue = graphData[i].y
          i++
        }
      }
      return tempGraphData
    },
    getGraphDataForScore(header, score) {
      return this.graphData[score]
        ? [
            this.isCumulativeDisplay[header]
              ? this.cumSum(this.graphData[score])
              : this.graphData[score],
            this.isNotBenchmarkAccount
              ? this.isCumulativeDisplay[header]
                ? this.cumSum(this.benchmarkGraphData[score])
                : this.benchmarkGraphData[score]
              : [],
          ]
        : []
    },
    onEditModalClose() {
      this.showEditModal = false
    },
    formatData(data, xKey, yKey, fn) {
      const tempData = data.map(obj => ({
        x: obj[xKey],
        y: fn ? fn(obj[yKey]) : obj[yKey],
      }))
      return tempData
    },
    cumSum(data) {
      if (!data) {
        return
      }
      let cumSumValue = 0
      const tempData = data.map(obj => {
        cumSumValue += obj.y
        return {
          ...obj,
          y: cumSumValue,
        }
      })
      return tempData
    },
    suffix(number) {
      const j = number % 10,
        k = number % 100

      if (j == 1 && k != 11) {
        return 'st'
      } else if (j == 2 && k != 12) {
        return 'nd'
      } else if (j == 3 && k != 13) {
        return 'rd'
      } else {
        return 'th'
      }
    },
    totalStakeToUSDToString(stake) {
      if (!stake) {
        return null
      }
      let totalStake = parseFloat(stake) || 0
      if (!totalStake) {
        return null
      }
      return (
        '$' +
        _.round(
          totalStake * parseFloat(this.nmrPrice).toPrecision(3),
          2,
        ).toLocaleString()
      )
    },
  },
}
</script>
<style lang="scss">
.tc {
  color: var(--color-true-contribution);
}

.benchmark-link {
  i,
  a {
    color: rgb(128 128 128) !important;
  }
  a {
    text-decoration: none !important;
    border-bottom: 1px dashed rgb(128 128 128 / 45%);
  }
}

.profile-container {
  &.isDarkMode {
    background-color: #000000;
  }

  .body-container {
    container-type: inline-size;
    container-name: body;
  }

  .border {
    width: calc(100% + 264px);
    margin-left: -132px;
    border-top: 1px solid var(--color-grey10);
  }

  .border.isDarkMode {
    border-top: 1px solid var(--color-grey50);
  }

  .heading {
    margin-bottom: 10px;
  }

  .account-meta {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .profile-pic-container {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 30px;
    flex-direction: column;
  }

  .profile-pic-container.editable:hover .profile-pic-edit {
    opacity: 1;
  }

  .profile-pic {
    flex-shrink: 0;
    background-position: center;
    background-size: cover;
    height: 225px;
    width: 225px;
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;

    &.noTitle {
      border-bottom-left-radius: 10px;
      border-bottom-right-radius: 10px;
    }
  }

  .gm-title {
    font-size: 16px;
    font-weight: 700;
    text-align: center;
    padding: 4px 0;
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;
    width: 225px;
    color: #ffffff;

    &.Grandmaster {
      background-color: var(--color-grandmaster);
      color: #000000;
    }

    &.Master {
      background-color: var(--color-master);
      color: #000000;
    }

    &.Expert {
      background-color: var(--color-expert);
      color: #000000;
    }

    &.Researcher {
      background-color: var(--color-researcher);
    }

    &.Contributor {
      background-color: var(--color-contributor);
    }

    &.Apprentice {
      background-color: var(--color-apprentice);
    }

    &.Novice {
      background-color: var(--color-novice);
    }
  }

  .profile-pic-edit {
    transition: all 0.2s;
    opacity: 0;
    background-color: rgba(0, 0, 0, 0.2);
    position: absolute;
    width: 225px;
    height: 225px;
    border-radius: 10px;
    top: 0;
    left: 0;
    right: 0;
    margin-left: auto;
    margin-right: auto;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .profile-pic.default {
    background-image: var(--default_profile_picture);
  }

  .team-badge {
    display: inline;
    width: 30px;
    margin-bottom: 0;

    &.darkMode {
      filter: invert(1);
    }
  }

  h1.account-name {
    all: unset;
    font-size: 40px;
    line-height: 1.2;
    font-weight: 600;
  }

  .inception {
    color: var(--color-grey40);
    font-size: 14px;
  }

  .stats {
    display: grid;
    grid-row-gap: 30px;
    grid-column-gap: 30px;
  }

  .stat {
    display: flex;
    flex-direction: column;
    align-items: center;

    .label {
      font-size: 16px;
      font-weight: 600;
    }

    .value {
      font-size: 40px;
      line-height: 1.2;
      font-weight: 500;
      color: var(--color-secondary);
      position: relative;
      margin-bottom: 3px;

      .superscript {
        position: absolute;
        top: 7px;
        font-size: 16px;
      }

      &.nmr {
        color: var(--color-text-nmr);
        white-space: nowrap;
      }

      &.return {
        color: var(--color-happy-green);
      }
    }

    .sub-label {
      font-size: 14px;
      color: var(--color-grey50);
    }
  }

  .section-container {
    margin: 60px 0;

    .section-title {
      font-size: 26px;
      font-weight: 700;
    }
  }

  .chart {
    min-height: 200px;
  }

  .kaggle-icon {
    display: flex;
    align-items: center;
    width: 16px;
    height: 16px;
    margin-top: 2px;
    margin-left: 2px;

    img {
      height: 12px;
      filter: invert(42%) sepia(25%) saturate(2872%) hue-rotate(181deg)
        brightness(89%) contrast(97%);
    }
  }

  .model-name {
    font-family: var(--brand-font);
    color: var(--color-primary);
  }

  .model-profile-pic {
    background-position: center;
    background-size: cover;
    width: 25px;
    height: 25px;
  }

  .default-pic {
    background-image: var(--default_profile_picture);
  }

  @container body (min-width: 565px) {
    .heading {
      display: flex;
      margin-bottom: 50px;
    }

    .profile-pic-container {
      margin-bottom: 0;
      margin-right: 40px;
    }

    .account-meta {
      display: block;
    }

    .stats {
      display: flex;
      justify-content: space-between;

      .stat {
        display: block;
      }
    }

    .chart {
      min-height: 330px;
    }
  }
}
</style>
